import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { LanguageConstants } from "../../constants/LanguageConstants";
import { NotificationPanel } from "./NotificationPanel";

export type NotificationPopupProps = {
  show: boolean;
  onHide: () => void;
};

export function NotificationPopup({ show, onHide }: NotificationPopupProps) {
  const [entered, setEntered] = useState(false);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const paginationRequestActiveRef = useRef(false);
  const paginationResetTimerRef = useRef<number | null>(null);
  const lastScrollTopRef = useRef(0);

  useEffect(() => {
    if (!show) {
      setEntered(false);
      return;
    }
    let id2 = 0;
    const id1 = window.requestAnimationFrame(() => {
      id2 = window.requestAnimationFrame(() => setEntered(true));
    });
    return () => {
      window.cancelAnimationFrame(id1);
      if (id2) window.cancelAnimationFrame(id2);
    };
  }, [show]);

  useEffect(() => {
    if (!show) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onHide();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [show, onHide]);

  useEffect(() => {
    if (!show) {
      setPaginationLoading(false);
      paginationRequestActiveRef.current = false;
      return;
    }

    const clearResetTimer = () => {
      if (paginationResetTimerRef.current != null) {
        window.clearTimeout(paginationResetTimerRef.current);
        paginationResetTimerRef.current = null;
      }
    };

    const getList = () => document.querySelector<HTMLDivElement>(".notification-popup .notification-list.refreshed");
    const getMoreButton = () =>
      document.querySelector<HTMLButtonElement>(".notification-popup .notification-panel__more-wrap button");
    const isSeeAllActive = () => Boolean(document.querySelector(".notification-popup .notification-see-all.del"));

    const finishPaginationLoad = () => {
      clearResetTimer();
      paginationRequestActiveRef.current = false;
      setPaginationLoading(false);
    };

    const requestNextPage = () => {
      if (paginationRequestActiveRef.current || !isSeeAllActive()) return;
      const moreButton = getMoreButton();
      if (!moreButton || moreButton.disabled) return;

      paginationRequestActiveRef.current = true;
      setPaginationLoading(true);
      moreButton.click();

      clearResetTimer();
      paginationResetTimerRef.current = window.setTimeout(finishPaginationLoad, 10000);
    };

    const handleScroll = () => {
      const list = getList();
      if (!list || paginationRequestActiveRef.current || !isSeeAllActive()) return;

      const currentScrollTop = list.scrollTop;
      const isScrollingDown = currentScrollTop > lastScrollTopRef.current + 4;
      lastScrollTopRef.current = currentScrollTop;
      if (!isScrollingDown) return;

      const distanceFromBottom = list.scrollHeight - list.scrollTop - list.clientHeight;
      if (distanceFromBottom <= 80) {
        requestNextPage();
      }
    };

    const observer = new MutationObserver(() => {
      if (paginationRequestActiveRef.current) {
        finishPaginationLoad();
      }
    });

    const setupId = window.setTimeout(() => {
      const list = getList();
      if (!list) return;

      lastScrollTopRef.current = list.scrollTop;
      list.addEventListener("scroll", handleScroll, { passive: true });
      observer.observe(list, { childList: true, subtree: true });
    }, 0);

    return () => {
      window.clearTimeout(setupId);
      clearResetTimer();
      const list = getList();
      list?.removeEventListener("scroll", handleScroll);
      observer.disconnect();
      paginationRequestActiveRef.current = false;
      lastScrollTopRef.current = 0;
      setPaginationLoading(false);
    };
  }, [show]);

  if (!show) return null;

  return createPortal(
    <div className="notification-popup" role="presentation">
      <button
        type="button"
        className={`notification-popup__backdrop${entered ? " is-visible" : ""}`}
        aria-label={LanguageConstants?.ARIA_CLOSE ?? "Close"}
        onClick={onHide}
      />
      <div
        className={`notification-popup__dialog${entered ? " is-open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="notification-popup-title"
      >
        <NotificationPanel isActive={show} onClose={onHide} />
        {paginationLoading ? (
          <div className="notification-popup__pagination-loader" aria-live="polite" aria-busy="true">
            Please wait...
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}

export default NotificationPopup;
